// Feedback & Roadmap – gemeinsame Seite für alle vier Module (Fehler melden, Funktionen wünschen, abstimmen, Roadmap).
// Backend: feedback-api (nginx der Module leitet /feedback-api/ dorthin). Auth über den gemeinsamen api-client.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { api } from '@pilot-hub/api-client'
import { useAuth } from '@pilot-hub/auth'

const BASE = '/feedback-api'
const APP_VERSION_FALLBACK = '1.2.052'

const fb = {
  get: (u: string, params?: Record<string, any>) => api.get(u, { baseURL: BASE, params }).then(r => r.data),
  post: (u: string, data?: any) => api.post(u, data ?? {}, { baseURL: BASE }).then(r => r.data),
  put: (u: string, data?: any) => api.put(u, data ?? {}, { baseURL: BASE }).then(r => r.data),
  patch: (u: string, data?: any) => api.patch(u, data ?? {}, { baseURL: BASE }).then(r => r.data),
  del: (u: string) => api.delete(u, { baseURL: BASE }).then(r => r.data),
}

function fehlerText(e: any): string {
  const d = e?.response?.data?.detail
  if (typeof d === 'string') return d
  return e?.message || 'Unbekannter Fehler'
}

function appVersion(): string {
  return (window as any).PILOT_HUB_CONFIG?.version || APP_VERSION_FALLBACK
}

// ---------------------------------------------------------------- Ungelesene Benachrichtigungen (Menü-Badge)
export function useFeedbackUngelesen(intervalMs = 60000): number {
  const [n, setN] = useState(0)
  const { user } = useAuth()
  const laden = useCallback(() => {
    fb.get('/benachrichtigungen/anzahl').then(d => setN(d.ungelesen || 0)).catch(() => {})
  }, [])
  useEffect(() => {
    if (!user) return
    laden()
    const t = window.setInterval(laden, intervalMs)
    window.addEventListener('feedback-gelesen', laden)
    return () => { window.clearInterval(t); window.removeEventListener('feedback-gelesen', laden) }
  }, [user, laden, intervalMs])
  return n
}

/** Eintrag im Nutzermenü jedes Moduls */
export function FeedbackMenuItem({ ungelesen, onDone }: { ungelesen: number; onDone?: () => void }) {
  const navigate = useNavigate()
  const location = useLocation()
  return (
    <button
      onClick={() => { onDone?.(); navigate('/feedback', { state: { von: location.pathname + location.search } }) }}
      className="flex w-full items-center justify-between px-4 py-3 text-left text-sm text-dark-text transition-colors hover:bg-dark-bg-tertiary"
    >
      <span>Feedback &amp; Roadmap</span>
      {ungelesen > 0 && (
        <span className="ml-2 min-w-[1.25rem] rounded-full bg-primary-600 px-1.5 py-0.5 text-center text-[11px] font-semibold text-white">{ungelesen}</span>
      )}
    </button>
  )
}

// ---------------------------------------------------------------- Typen & Anzeige
interface Eintrag {
  id: number; typ: 'FEHLER' | 'WUNSCH'; titel: string; beschreibung?: string | null; status: string
  zielversion?: string | null; umgesetzt_in_version?: string | null; duplikat_von?: number | null; duplikat_titel?: string | null
  modul?: string | null; seite_url?: string; app_version?: string; user_agent?: string; bildschirm?: string; melder?: string
  erstellt_am: string; status_geaendert_am: string; stimmen: number; ich_gestimmt: boolean; meiner: boolean
  kommentare: number; begruendung?: string | null
  kommentare_liste?: { id: number; text: string; ist_begruendung: boolean; erstellt_am: string }[]
  verlauf?: { von_status: string | null; nach_status: string; version?: string | null; erstellt_am: string }[]
}

const STATUS_LABEL: Record<string, string> = {
  NEU: 'Neu', IN_PRUEFUNG: 'In Prüfung', GEPLANT: 'Geplant', IN_ARBEIT: 'In Arbeit', UMGESETZT: 'Umgesetzt',
  ABGELEHNT: 'Abgelehnt', DUPLIKAT: 'Duplikat', BESTAETIGT: 'Bestätigt', BEHOBEN: 'Behoben',
  NICHT_REPRODUZIERBAR: 'Nicht reproduzierbar', KEIN_FEHLER: 'Kein Fehler',
}
const STATUS_FARBE: Record<string, string> = {
  NEU: 'bg-dark-bg-tertiary text-dark-text-muted', IN_PRUEFUNG: 'bg-blue-900/30 text-blue-300', GEPLANT: 'bg-indigo-900/30 text-indigo-300',
  IN_ARBEIT: 'bg-yellow-900/30 text-yellow-300', UMGESETZT: 'bg-green-900/30 text-green-400', BEHOBEN: 'bg-green-900/30 text-green-400',
  BESTAETIGT: 'bg-blue-900/30 text-blue-300', ABGELEHNT: 'bg-red-900/30 text-red-400', DUPLIKAT: 'bg-dark-bg-tertiary text-dark-text-muted',
  NICHT_REPRODUZIERBAR: 'bg-dark-bg-tertiary text-dark-text-muted', KEIN_FEHLER: 'bg-dark-bg-tertiary text-dark-text-muted',
}
const STATUS_WUNSCH = ['NEU', 'IN_PRUEFUNG', 'GEPLANT', 'IN_ARBEIT', 'UMGESETZT', 'ABGELEHNT', 'DUPLIKAT']
const STATUS_FEHLER = ['NEU', 'BESTAETIGT', 'IN_ARBEIT', 'BEHOBEN', 'NICHT_REPRODUZIERBAR', 'KEIN_FEHLER']
const MODUL_LABEL: Record<string, string> = { admin: 'Admin', fuehrung: 'Führung', pilot: 'Pilot', leitstelle: 'Leitstelle' }

const datum = (s?: string | null) => (s ? new Date(s).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '')

function versionVergleich(a: string, b: string): number {
  const pa = a.split('.').map(x => parseInt(x, 10) || 0); const pb = b.split('.').map(x => parseInt(x, 10) || 0)
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) { const d = (pa[i] || 0) - (pb[i] || 0); if (d) return d }
  return 0
}

const INPUT = 'w-full rounded-lg border border-dark-border bg-dark-bg px-3 py-2.5 text-sm text-dark-text placeholder:text-dark-text-subtle focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
const BTN = 'rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50'
const BTN2 = 'rounded-lg border border-dark-border bg-dark-bg-tertiary px-4 py-2.5 text-sm font-medium text-dark-text transition-colors hover:bg-dark-bg disabled:opacity-50'

function StatusBadge({ status }: { status: string }) {
  return <span className={`inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_FARBE[status] || STATUS_FARBE.NEU}`}>{STATUS_LABEL[status] || status}</span>
}

function TypBadge({ typ }: { typ: string }) {
  return typ === 'FEHLER'
    ? <span className="inline-block rounded-full bg-red-900/30 px-2.5 py-0.5 text-xs font-medium text-red-300">Fehler</span>
    : <span className="inline-block rounded-full bg-blue-900/30 px-2.5 py-0.5 text-xs font-medium text-blue-300">Wunsch</span>
}

function StimmenButton({ e, onChange }: { e: Eintrag; onChange: (n: Eintrag) => void }) {
  const [busy, setBusy] = useState(false)
  const [fehler, setFehler] = useState('')
  const zu = ['ABGELEHNT', 'DUPLIKAT', 'UMGESETZT'].includes(e.status)
  const klick = async (ev: React.MouseEvent) => {
    ev.stopPropagation()
    setBusy(true); setFehler('')
    try { onChange(e.ich_gestimmt ? await fb.del(`/eintraege/${e.id}/stimme`) : await fb.post(`/eintraege/${e.id}/stimme`)) }
    catch (x) { setFehler(fehlerText(x)) } finally { setBusy(false) }
  }
  return (
    <div className="flex shrink-0 flex-col items-center gap-1">
      <button
        onClick={klick} disabled={busy || zu}
        title={e.ich_gestimmt ? 'Stimme zurücknehmen' : 'Brauche ich auch'}
        className={`flex min-h-[3.25rem] min-w-[4.5rem] flex-col items-center justify-center rounded-xl border px-3 py-1.5 text-center transition-colors disabled:opacity-60 ${e.ich_gestimmt ? 'border-primary-500 bg-primary-600/20 text-primary-200' : 'border-dark-border bg-dark-bg-tertiary text-dark-text hover:border-primary-500'}`}
      >
        <span className="text-lg font-semibold leading-none">{e.stimmen}</span>
        <span className="mt-1 text-[11px] leading-tight">{e.ich_gestimmt ? 'Gestimmt ✓' : 'Brauche ich auch'}</span>
      </button>
      {fehler && <span className="max-w-[8rem] text-center text-[11px] text-red-400">{fehler}</span>}
    </div>
  )
}

function Karte({ e, onOpen, onChange, zeigeTyp, zeigeBegruendung }: {
  e: Eintrag; onOpen: (id: number) => void; onChange: (n: Eintrag) => void; zeigeTyp?: boolean; zeigeBegruendung?: boolean
}) {
  return (
    <div onClick={() => onOpen(e.id)} className="flex cursor-pointer items-start gap-3 rounded-xl border border-dark-border bg-dark-bg p-3 transition-colors hover:border-primary-500/60">
      {e.typ === 'WUNSCH' && <StimmenButton e={e} onChange={onChange} />}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {zeigeTyp && <TypBadge typ={e.typ} />}
          <StatusBadge status={e.status} />
          {e.modul && <span className="text-xs text-dark-text-subtle">{MODUL_LABEL[e.modul] || e.modul}</span>}
          {e.zielversion && <span className="text-xs text-dark-text-subtle">Ziel {e.zielversion}</span>}
          {e.umgesetzt_in_version && <span className="text-xs text-dark-text-subtle">in {e.umgesetzt_in_version}</span>}
        </div>
        <div className="mt-1 break-words font-medium text-dark-text">{e.titel}</div>
        {e.beschreibung && <div className="mt-0.5 line-clamp-2 whitespace-pre-line break-words text-sm text-dark-text-muted">{e.beschreibung}</div>}
        {zeigeBegruendung && e.begruendung && <div className="mt-2 rounded-lg bg-dark-bg-tertiary px-3 py-2 text-sm text-dark-text"><span className="text-dark-text-subtle">Begründung: </span>{e.begruendung}</div>}
        <div className="mt-1 text-xs text-dark-text-subtle">
          {datum(e.erstellt_am)}{e.meiner ? ' · von dir' : ''}{e.melder && !e.meiner ? ` · ${e.melder}` : ''}{e.kommentare > 0 ? ` · ${e.kommentare} Antwort${e.kommentare > 1 ? 'en' : ''}` : ''}
        </div>
      </div>
    </div>
  )
}

function Leer({ text }: { text: string }) {
  return <div className="rounded-xl border border-dashed border-dark-border p-8 text-center text-sm text-dark-text-muted">{text}</div>
}

// ---------------------------------------------------------------- Melden
function MeldenTab({ modul, von, onGemeldet, onOpen }: { modul: string; von: string; onGemeldet: () => void; onOpen: (id: number) => void }) {
  const [typ, setTyp] = useState<'FEHLER' | 'WUNSCH'>('WUNSCH')
  const [titel, setTitel] = useState('')
  const [beschr, setBeschr] = useState('')
  const [aehnliche, setAehnliche] = useState<Eintrag[]>([])
  const [busy, setBusy] = useState(false)
  const [fehler, setFehler] = useState('')
  const [ok, setOk] = useState('')
  const info = useMemo(() => ({
    modul, seite_url: von, app_version: appVersion(), user_agent: navigator.userAgent.slice(0, 480),
    bildschirm: `${window.innerWidth}x${window.innerHeight} @${window.devicePixelRatio || 1}x`,
  }), [modul, von])

  useEffect(() => {
    if (typ !== 'WUNSCH' || titel.trim().length < 3) { setAehnliche([]); return }
    const t = window.setTimeout(() => { fb.get('/aehnliche', { titel }).then(d => setAehnliche(Array.isArray(d) ? d : [])).catch(() => setAehnliche([])) }, 400)
    return () => window.clearTimeout(t)
  }, [titel, typ])

  const senden = async () => {
    setBusy(true); setFehler(''); setOk('')
    try {
      await fb.post('/eintraege', { typ, titel, beschreibung: beschr, ...info })
      setTitel(''); setBeschr(''); setAehnliche([])
      setOk(typ === 'FEHLER' ? 'Danke! Dein Fehler wurde gemeldet.' : 'Danke! Dein Wunsch wurde eingetragen.')
      onGemeldet()
    } catch (e) { setFehler(fehlerText(e)) } finally { setBusy(false) }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {([['FEHLER', '🐞', 'Fehler melden', 'Etwas funktioniert nicht richtig'], ['WUNSCH', '💡', 'Funktion wünschen', 'Das fehlt mir noch']] as const).map(([k, ic, t, s]) => (
          <button key={k} onClick={() => setTyp(k)}
            className={`rounded-2xl border-2 p-4 text-left transition-colors ${typ === k ? 'border-primary-500 bg-primary-600/15' : 'border-dark-border bg-dark-bg hover:border-dark-text-subtle'}`}>
            <div className="text-2xl">{ic}</div>
            <div className="mt-1 font-semibold text-dark-text">{t}</div>
            <div className="text-xs text-dark-text-muted">{s}</div>
          </button>
        ))}
      </div>
      {typ === 'FEHLER' && <div className="rounded-lg bg-dark-bg-tertiary px-3 py-2 text-xs text-dark-text-muted">Fehlermeldungen sehen nur du und der Entwickler.</div>}
      <div>
        <label className="mb-1 block text-sm text-dark-text-muted">Titel</label>
        <input className={INPUT} value={titel} maxLength={200} onChange={e => { setTitel(e.target.value); setOk('') }}
          placeholder={typ === 'FEHLER' ? 'Was ist passiert?' : 'Was wünschst du dir?'} />
      </div>
      {aehnliche.length > 0 && (
        <div className="rounded-xl border border-yellow-700/50 bg-yellow-900/10 p-3">
          <div className="mb-2 text-sm text-yellow-300">Gibt es schon etwas Ähnliches? Dann stimme einfach dafür ab:</div>
          <div className="space-y-2">
            {aehnliche.map(a => (
              <Karte key={a.id} e={a} onOpen={onOpen} onChange={n => setAehnliche(l => l.map(x => x.id === n.id ? n : x))} />
            ))}
          </div>
        </div>
      )}
      <div>
        <label className="mb-1 block text-sm text-dark-text-muted">Beschreibung</label>
        <textarea className={INPUT} rows={6} value={beschr} onChange={e => setBeschr(e.target.value)}
          placeholder={typ === 'FEHLER' ? 'Was hast du gemacht, was ist passiert, was hast du erwartet?' : 'Wofür brauchst du das? Wie sollte es funktionieren?'} />
      </div>
      <div className="rounded-lg border border-dark-border px-3 py-2 text-xs text-dark-text-subtle">
        Wird automatisch mitgeschickt: Modul {MODUL_LABEL[modul] || modul} · Seite {von || '–'} · Version {info.app_version} · Bildschirm {info.bildschirm}
        <div className="mt-0.5 break-all">Gerät/Browser: {info.user_agent}</div>
      </div>
      {fehler && <div className="rounded-lg bg-red-900/20 px-3 py-2 text-sm text-red-400">{fehler}</div>}
      {ok && <div className="rounded-lg bg-green-900/20 px-3 py-2 text-sm text-green-400">{ok}</div>}
      <button className={BTN + ' w-full sm:w-auto'} disabled={busy || titel.trim().length < 3} onClick={senden}>
        {busy ? 'Sende …' : typ === 'FEHLER' ? 'Fehler absenden' : 'Wunsch absenden'}
      </button>
    </div>
  )
}

// ---------------------------------------------------------------- Listen
function useListe(pfad: string, params?: Record<string, any>, dep: any = 0) {
  const [items, setItems] = useState<Eintrag[] | null>(null)
  const [fehler, setFehler] = useState('')
  const key = JSON.stringify(params || {})
  const laden = useCallback(() => {
    fb.get(pfad, params).then(d => { setItems(Array.isArray(d) ? d : []); setFehler('') }).catch(e => setFehler(fehlerText(e)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pfad, key])
  useEffect(() => { laden() }, [laden, dep])
  const ersetze = (n: Eintrag) => setItems(l => l && l.map(x => x.id === n.id ? n : x))
  return { items, fehler, laden, ersetze }
}

function WuenscheTab({ onOpen, dep }: { onOpen: (id: number) => void; dep: any }) {
  const [q, setQ] = useState('')
  const [sort, setSort] = useState('stimmen')
  const [qEff, setQEff] = useState('')
  useEffect(() => { const t = window.setTimeout(() => setQEff(q), 300); return () => window.clearTimeout(t) }, [q])
  const { items, fehler, ersetze } = useListe('/wuensche', { q: qEff, sort }, dep)
  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row">
        <input className={INPUT} placeholder="Wünsche durchsuchen …" value={q} onChange={e => setQ(e.target.value)} />
        <select className={INPUT + ' sm:w-56'} value={sort} onChange={e => setSort(e.target.value)}>
          <option value="stimmen">Meiste Stimmen</option>
          <option value="neu">Neueste zuerst</option>
        </select>
      </div>
      {fehler && <div className="text-sm text-red-400">{fehler}</div>}
      {items === null ? <Leer text="Lade …" /> : items.length === 0 ? <Leer text="Noch keine Wünsche vorhanden." /> :
        items.map(e => <Karte key={e.id} e={e} onOpen={onOpen} onChange={ersetze} />)}
    </div>
  )
}

function RoadmapTab({ onOpen, dep }: { onOpen: (id: number) => void; dep: any }) {
  const { items, fehler, ersetze } = useListe('/roadmap', undefined, dep)
  if (items === null) return <Leer text={fehler || 'Lade …'} />
  const geplant = items.filter(e => e.status === 'GEPLANT')
  const inArbeit = items.filter(e => e.status === 'IN_ARBEIT')
  const fertig = items.filter(e => e.status === 'UMGESETZT')
  const gruppen: Record<string, Eintrag[]> = {}
  fertig.forEach(e => { const v = e.umgesetzt_in_version || 'ohne Version'; (gruppen[v] = gruppen[v] || []).push(e) })
  const versionen = Object.keys(gruppen).sort((a, b) => versionVergleich(b, a))
  const spalte = (titel: string, farbe: string, inhalt: React.ReactNode, n: number) => (
    <div className="min-w-0 rounded-2xl border border-dark-border bg-dark-bg-secondary p-3">
      <div className={`mb-3 flex items-center justify-between border-b pb-2 ${farbe}`}><span className="font-semibold text-dark-text">{titel}</span><span className="text-xs text-dark-text-muted">{n}</span></div>
      <div className="space-y-2">{inhalt}</div>
    </div>
  )
  const liste = (l: Eintrag[]) => l.length === 0 ? <div className="py-4 text-center text-sm text-dark-text-subtle">–</div> :
    l.map(e => <Karte key={e.id} e={e} onOpen={onOpen} onChange={ersetze} />)
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      {spalte('Geplant', 'border-indigo-500/50', liste([...geplant].sort((a, b) => (a.zielversion ? 0 : 1) - (b.zielversion ? 0 : 1) || b.stimmen - a.stimmen)), geplant.length)}
      {spalte('In Arbeit', 'border-yellow-500/50', liste(inArbeit), inArbeit.length)}
      {spalte('Umgesetzt', 'border-green-500/50', fertig.length === 0 ? liste([]) : versionen.map(v => (
        <div key={v}>
          <div className="mb-1.5 mt-1 text-xs font-semibold uppercase tracking-wider text-dark-text-subtle">Version {v}</div>
          <div className="space-y-2">{gruppen[v].map(e => <Karte key={e.id} e={e} onOpen={onOpen} onChange={ersetze} />)}</div>
        </div>)), fertig.length)}
    </div>
  )
}

function AbgelehntTab({ onOpen, dep }: { onOpen: (id: number) => void; dep: any }) {
  const { items, fehler, ersetze } = useListe('/abgelehnt', undefined, dep)
  if (items === null) return <Leer text={fehler || 'Lade …'} />
  return <div className="space-y-3">{items.length === 0 ? <Leer text="Keine abgelehnten Wünsche." /> :
    items.map(e => <Karte key={e.id} e={e} onOpen={onOpen} onChange={ersetze} zeigeBegruendung />)}</div>
}

function MeineTab({ onOpen, dep }: { onOpen: (id: number) => void; dep: any }) {
  const { items, fehler, ersetze } = useListe('/meine', undefined, dep)
  if (items === null) return <Leer text={fehler || 'Lade …'} />
  return <div className="space-y-3">{items.length === 0 ? <Leer text="Du hast noch nichts gemeldet." /> :
    items.map(e => <Karte key={e.id} e={e} onOpen={onOpen} onChange={ersetze} zeigeTyp zeigeBegruendung />)}</div>
}

function VerwaltungTab({ onOpen, dep }: { onOpen: (id: number) => void; dep: any }) {
  const [typ, setTyp] = useState(''); const [status, setStatus] = useState(''); const [modul, setModul] = useState(''); const [q, setQ] = useState('')
  const [qEff, setQEff] = useState('')
  useEffect(() => { const t = window.setTimeout(() => setQEff(q), 300); return () => window.clearTimeout(t) }, [q])
  const { items, fehler, ersetze } = useListe('/verwaltung', { typ, status, modul, q: qEff }, dep)
  const alleStatus = typ === 'FEHLER' ? STATUS_FEHLER : typ === 'WUNSCH' ? STATUS_WUNSCH : Array.from(new Set([...STATUS_WUNSCH, ...STATUS_FEHLER]))
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <select className={INPUT} value={typ} onChange={e => { setTyp(e.target.value); setStatus('') }}>
          <option value="">Alle Typen</option><option value="FEHLER">Fehler</option><option value="WUNSCH">Wünsche</option>
        </select>
        <select className={INPUT} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">Alle Status</option>{alleStatus.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
        <select className={INPUT} value={modul} onChange={e => setModul(e.target.value)}>
          <option value="">Alle Module</option>{Object.entries(MODUL_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input className={INPUT} placeholder="Suche …" value={q} onChange={e => setQ(e.target.value)} />
      </div>
      {fehler && <div className="text-sm text-red-400">{fehler}</div>}
      {items === null ? <Leer text="Lade …" /> : items.length === 0 ? <Leer text="Keine Einträge für diesen Filter." /> :
        items.map(e => <Karte key={e.id} e={e} onOpen={onOpen} onChange={ersetze} zeigeTyp />)}
    </div>
  )
}

// ---------------------------------------------------------------- Detail
function VerwaltungsPanel({ e, onChange }: { e: Eintrag; onChange: (n: Eintrag) => void }) {
  const [status, setStatus] = useState(e.status)
  const [version, setVersion] = useState(e.umgesetzt_in_version || appVersion())
  const [ziel, setZiel] = useState(e.zielversion || '')
  const [begr, setBegr] = useState('')
  const [dup, setDup] = useState<string>(e.duplikat_von ? String(e.duplikat_von) : '')
  const [wuensche, setWuensche] = useState<Eintrag[]>([])
  const [versionen, setVersionen] = useState<string[]>([])
  const [komm, setKomm] = useState('')
  const [busy, setBusy] = useState(false); const [fehler, setFehler] = useState(''); const [ok, setOk] = useState('')
  const optionen = e.typ === 'WUNSCH' ? STATUS_WUNSCH : STATUS_FEHLER
  const brauchtVersion = status === 'UMGESETZT' || status === 'BEHOBEN'
  const brauchtBegr = status === 'ABGELEHNT'

  useEffect(() => {
    fb.get('/versionen').then(d => setVersionen(Array.isArray(d) ? d : [])).catch(() => {})
    if (e.typ === 'WUNSCH') fb.get('/verwaltung', { typ: 'WUNSCH' }).then((l0: Eintrag[]) => setWuensche((Array.isArray(l0) ? l0 : []).filter(x => x.id !== e.id && x.status !== 'DUPLIKAT'))).catch(() => {})
  }, [e.id, e.typ])

  const setzen = async () => {
    setBusy(true); setFehler(''); setOk('')
    try {
      const n = await fb.post(`/eintraege/${e.id}/status`, {
        status, version: brauchtVersion ? version : null, zielversion: status === 'GEPLANT' || status === 'IN_ARBEIT' ? ziel : null,
        begruendung: begr, duplikat_von: status === 'DUPLIKAT' ? Number(dup) || null : null,
      })
      setBegr(''); setOk('Status gespeichert.'); onChange(n)
    } catch (x) { setFehler(fehlerText(x)) } finally { setBusy(false) }
  }
  const kommentieren = async () => {
    setBusy(true); setFehler(''); setOk('')
    try { await fb.post(`/eintraege/${e.id}/kommentare`, { text: komm }); setKomm(''); setOk('Antwort gespeichert.'); onChange({ ...e }) }
    catch (x) { setFehler(fehlerText(x)) } finally { setBusy(false) }
  }
  return (
    <div className="space-y-3 rounded-xl border border-primary-600/40 bg-primary-600/5 p-3">
      <div className="text-sm font-semibold text-dark-text">Verwaltung (Entwickler)</div>
      <datalist id="fb-versionen">{Array.from(new Set([appVersion(), ...versionen])).map(v => <option key={v} value={v} />)}</datalist>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-dark-text-muted">Status</label>
          <select className={INPUT} value={status} onChange={x => setStatus(x.target.value)}>{optionen.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}</select>
        </div>
        {brauchtVersion && (
          <div>
            <label className="mb-1 block text-xs text-dark-text-muted">{status === 'BEHOBEN' ? 'Behoben in Version' : 'Umgesetzt in Version'} (Pflicht)</label>
            <input className={INPUT} list="fb-versionen" value={version} onChange={x => setVersion(x.target.value)} />
          </div>
        )}
        {(status === 'GEPLANT' || status === 'IN_ARBEIT') && (
          <div>
            <label className="mb-1 block text-xs text-dark-text-muted">Zielversion (optional)</label>
            <input className={INPUT} list="fb-versionen" value={ziel} onChange={x => setZiel(x.target.value)} placeholder="z. B. 1.2.052" />
          </div>
        )}
        {status === 'DUPLIKAT' && (
          <div>
            <label className="mb-1 block text-xs text-dark-text-muted">Duplikat von (Pflicht) – Stimmen werden übertragen</label>
            <select className={INPUT} value={dup} onChange={x => setDup(x.target.value)}>
              <option value="">– Original wählen –</option>
              {wuensche.map(w => <option key={w.id} value={w.id}>#{w.id} {w.titel.slice(0, 60)}</option>)}
            </select>
          </div>
        )}
      </div>
      <div>
        <label className="mb-1 block text-xs text-dark-text-muted">{brauchtBegr ? 'Begründung (Pflicht, sichtbar für alle)' : 'Begründung / Antwort zum Status (optional)'}</label>
        <textarea className={INPUT} rows={2} value={begr} onChange={x => setBegr(x.target.value)} />
      </div>
      <button className={BTN} disabled={busy} onClick={setzen}>Status speichern</button>
      <div className="border-t border-dark-border pt-3">
        <label className="mb-1 block text-xs text-dark-text-muted">Antwort schreiben (Melder wird benachrichtigt)</label>
        <textarea className={INPUT} rows={2} value={komm} onChange={x => setKomm(x.target.value)} />
        <button className={BTN2 + ' mt-2'} disabled={busy || !komm.trim()} onClick={kommentieren}>Antwort senden</button>
      </div>
      {fehler && <div className="text-sm text-red-400">{fehler}</div>}
      {ok && <div className="text-sm text-green-400">{ok}</div>}
    </div>
  )
}

function DetailDialog({ id, istEntwickler, onClose, onChanged }: { id: number; istEntwickler: boolean; onClose: () => void; onChanged: () => void }) {
  const [e, setE] = useState<Eintrag | null>(null)
  const [fehler, setFehler] = useState('')
  const [edit, setEdit] = useState(false)
  const [titel, setTitel] = useState(''); const [beschr, setBeschr] = useState('')
  const laden = useCallback(() => {
    fb.get(`/eintraege/${id}`).then(d => { setE(d); setTitel(d.titel); setBeschr(d.beschreibung || '') }).catch(x => setFehler(fehlerText(x)))
  }, [id])
  useEffect(() => { laden() }, [laden])
  const geaendert = (n: Eintrag) => { laden(); onChanged(); void n }
  const speichern = async () => {
    try { await fb.patch(`/eintraege/${id}`, { titel, beschreibung: beschr }); setEdit(false); laden(); onChanged() }
    catch (x) { setFehler(fehlerText(x)) }
  }
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl border border-dark-border bg-dark-bg-secondary p-4 sm:rounded-2xl sm:p-5" onClick={ev => ev.stopPropagation()}>
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">{e && <><TypBadge typ={e.typ} /><StatusBadge status={e.status} /></>}</div>
          <button onClick={onClose} className="rounded-lg p-2 text-dark-text-muted hover:bg-dark-bg-tertiary hover:text-dark-text" aria-label="Schließen">✕</button>
        </div>
        {fehler && <div className="mb-2 text-sm text-red-400">{fehler}</div>}
        {!e ? <Leer text="Lade …" /> : (
          <div className="space-y-4">
            {edit ? (
              <div className="space-y-2">
                <input className={INPUT} value={titel} onChange={x => setTitel(x.target.value)} />
                <textarea className={INPUT} rows={5} value={beschr} onChange={x => setBeschr(x.target.value)} />
                <div className="flex gap-2"><button className={BTN} onClick={speichern}>Speichern</button><button className={BTN2} onClick={() => setEdit(false)}>Abbrechen</button></div>
              </div>
            ) : (
              <div>
                <h2 className="break-words text-lg font-semibold text-dark-text">{e.titel}</h2>
                {e.beschreibung && <p className="mt-2 whitespace-pre-line break-words text-sm text-dark-text-muted">{e.beschreibung}</p>}
                {(istEntwickler || (e.meiner && e.status === 'NEU')) && <button className="mt-2 text-sm text-primary-300 hover:underline" onClick={() => setEdit(true)}>Bearbeiten</button>}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-dark-text-muted">
              {e.typ === 'WUNSCH' && <StimmenButton e={e} onChange={n => { setE({ ...e, ...n, kommentare_liste: e.kommentare_liste, verlauf: e.verlauf }); onChanged() }} />}
              <span>Gemeldet {datum(e.erstellt_am)}{e.melder ? ` von ${e.melder}` : ''}</span>
              {e.zielversion && <span>Zielversion {e.zielversion}</span>}
              {e.umgesetzt_in_version && <span>{e.typ === 'FEHLER' ? 'Behoben' : 'Umgesetzt'} in Version {e.umgesetzt_in_version}</span>}
              {e.duplikat_von && <span>Duplikat von #{e.duplikat_von} {e.duplikat_titel}</span>}
            </div>
            {(istEntwickler || e.meiner) && (e.seite_url || e.app_version || e.user_agent) && (
              <div className="rounded-lg border border-dark-border p-3 text-xs text-dark-text-subtle">
                <div>Modul: {MODUL_LABEL[e.modul || ''] || e.modul || '–'} · Seite: {e.seite_url || '–'} · Version: {e.app_version || '–'} · Bildschirm: {e.bildschirm || '–'}</div>
                <div className="mt-0.5 break-all">Gerät/Browser: {e.user_agent || '–'}</div>
              </div>
            )}
            <div>
              <div className="mb-2 text-sm font-semibold text-dark-text">Antworten des Entwicklers</div>
              {(e.kommentare_liste || []).length === 0 ? <div className="text-sm text-dark-text-subtle">Noch keine Antwort.</div> :
                <div className="space-y-2">{e.kommentare_liste!.map(k => (
                  <div key={k.id} className="rounded-lg bg-dark-bg-tertiary px-3 py-2">
                    <div className="mb-0.5 text-xs text-dark-text-subtle">{datum(k.erstellt_am)}{k.ist_begruendung ? ' · Begründung' : ''}</div>
                    <div className="whitespace-pre-line break-words text-sm text-dark-text">{k.text}</div>
                  </div>))}</div>}
            </div>
            <div>
              <div className="mb-2 text-sm font-semibold text-dark-text">Status-Verlauf</div>
              <ul className="space-y-1 text-sm text-dark-text-muted">
                {(e.verlauf || []).map((v, i) => (
                  <li key={i}>{datum(v.erstellt_am)} · {v.von_status ? `${STATUS_LABEL[v.von_status] || v.von_status} → ` : ''}<span className="text-dark-text">{STATUS_LABEL[v.nach_status] || v.nach_status}</span>{v.version ? ` (${v.version})` : ''}</li>
                ))}
              </ul>
            </div>
            {istEntwickler && <VerwaltungsPanel e={e} onChange={geaendert} />}
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- Seite
type Tab = 'melden' | 'wuensche' | 'roadmap' | 'abgelehnt' | 'meine' | 'verwaltung'

interface Benachrichtigung { id: number; eintrag_id: number | null; text: string; gelesen: boolean; erstellt_am: string }

export function FeedbackPage({ modul }: { modul: string }) {
  const location = useLocation()
  const von = ((location.state as any)?.von as string) || ''
  const [tab, setTab] = useState<Tab>('melden')
  const [istEntwickler, setIstEntwickler] = useState(false)
  const [detail, setDetail] = useState<number | null>(null)
  const [dep, setDep] = useState(0)
  const [meldungen, setMeldungen] = useState<Benachrichtigung[]>([])
  const [zeigeMeldungen, setZeigeMeldungen] = useState(false)
  const gestartet = useRef(false)
  const neu = () => setDep(d => d + 1)

  useEffect(() => {
    if (gestartet.current) return
    gestartet.current = true
    fb.get('/me').then(m => setIstEntwickler(!!m.ist_entwickler)).catch(() => {})
    fb.get('/benachrichtigungen').then((l0: Benachrichtigung[]) => {
      const l = Array.isArray(l0) ? l0 : []
      setMeldungen(l)
      if (l.some(x => !x.gelesen)) {
        setZeigeMeldungen(true)
        // „gelesen“ beim Öffnen; die Liste zeigt für diese Ansicht weiterhin an, was neu war
        fb.post('/benachrichtigungen/gelesen', {}).then(() => window.dispatchEvent(new Event('feedback-gelesen'))).catch(() => {})
      }
    }).catch(() => {})
  }, [])

  const tabs: [Tab, string][] = [['melden', 'Melden'], ['wuensche', 'Wünsche'], ['roadmap', 'Roadmap'], ['abgelehnt', 'Abgelehnt'], ['meine', 'Meine Meldungen']]
  if (istEntwickler) tabs.push(['verwaltung', 'Verwaltung'])
  const ungelesen = meldungen.filter(m => !m.gelesen).length

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-dark-text">Feedback &amp; Roadmap</h2>
          <p className="text-sm text-dark-text-muted">Fehler melden, Funktionen wünschen und sehen, was als Nächstes kommt.</p>
        </div>
        <button onClick={() => setZeigeMeldungen(z => !z)} className={BTN2 + ' relative shrink-0'} aria-label="Benachrichtigungen">
          🔔
          {ungelesen > 0 && <span className="absolute -right-1.5 -top-1.5 min-w-[1.25rem] rounded-full bg-primary-600 px-1 text-center text-[11px] font-semibold text-white">{ungelesen}</span>}
        </button>
      </div>

      {zeigeMeldungen && (
        <div className="rounded-xl border border-dark-border bg-dark-bg p-3">
          <div className="mb-2 text-sm font-semibold text-dark-text">Benachrichtigungen</div>
          {meldungen.length === 0 ? <div className="text-sm text-dark-text-subtle">Keine Benachrichtigungen.</div> : (
            <ul className="space-y-1.5">
              {meldungen.slice(0, 15).map(m => (
                <li key={m.id}>
                  <button disabled={!m.eintrag_id} onClick={() => m.eintrag_id && setDetail(m.eintrag_id)}
                    className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-dark-bg-tertiary ${m.gelesen ? 'text-dark-text-muted' : 'border-l-4 border-primary-500 bg-primary-600/10 text-dark-text'}`}>
                    <div className="break-words">{m.text}</div>
                    <div className="text-xs text-dark-text-subtle">{datum(m.erstellt_am)}</div>
                  </button>
                </li>))}
            </ul>)}
        </div>
      )}

      <div className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-1">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${tab === k ? 'bg-primary-600 text-white' : 'bg-dark-bg-tertiary text-dark-text-muted hover:text-dark-text'}`}>{l}</button>
        ))}
      </div>

      {tab === 'melden' && <MeldenTab modul={modul} von={von} onGemeldet={neu} onOpen={setDetail} />}
      {tab === 'wuensche' && <WuenscheTab onOpen={setDetail} dep={dep} />}
      {tab === 'roadmap' && <RoadmapTab onOpen={setDetail} dep={dep} />}
      {tab === 'abgelehnt' && <AbgelehntTab onOpen={setDetail} dep={dep} />}
      {tab === 'meine' && <MeineTab onOpen={setDetail} dep={dep} />}
      {tab === 'verwaltung' && istEntwickler && <VerwaltungTab onOpen={setDetail} dep={dep} />}

      {detail !== null && <DetailDialog id={detail} istEntwickler={istEntwickler} onClose={() => setDetail(null)} onChanged={neu} />}
    </div>
  )
}
